import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import api from "./api";
import {handleResponseStatus, updateIdField, updateItem} from "./utils";
import {mergeProperties} from "../components/Utility";

const initialState = {
    items: [],
    finalData: {},
    initialData: {},
    fetchStatus: "idle",// 'idle' | 'loading' | 'success' | 'failed'
    createStatus: "idle",// 'idle' | 'loading' | 'success' | 'failed'
    updateStatus: "idle",// 'idle' | 'loading' | 'success' | 'failed'
    fetchByIdStatus: "idle",// 'idle' | 'loading' | 'success' | 'failed'
    deleteStatus: "idle",// 'idle' | 'loading' | 'success' | 'failed'
    markAsPaidStatus: "idle",// 'idle' | 'loading' | 'success' | 'failed'
    pagination: {},
    error: null
};

//Create Invoice
export const createPdfData = createAsyncThunk('manualPdf/createPdfData', async ({roleSign, data}) => {
    try {
        const response = await api.post(`/${roleSign}/invoices`, data);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

//Fetch All Invoices
export const fetchAllInvoices = createAsyncThunk(
    'manualPdf/fetchAllInvoices',
    async ({ scopeId, roleSign, page, limit, filterByStatus }) => {
        try {
            let url = `/${roleSign}/invoices?id=${scopeId}&page=${page}&limit=${limit}`;

            // Add status param only if it's 0 or 1
            if (filterByStatus === 0 || filterByStatus === 1) {
                url += `&status=${filterByStatus}`;
            }

            const response = await api.get(url);
            return { ...response };
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Fetch Invoice by ID
export const fetchInvoiceById = createAsyncThunk('manualPdf/fetchInvoiceById',
    async ({id, roleSign}) => {
        try {
            const response = await api.get(`/${roleSign}/invoices/${id}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

export const updateInvoice = createAsyncThunk('manualPdf/updateInvoice', async ({
                                                                                    roleSign,
                                                                                    data,
                                                                                    id
                                                                                }) => {
    try {
        let response = await api.patch(`/${roleSign}/invoices/${id}`, data);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});


// Delete Invoice
export const deleteInvoice = createAsyncThunk('manualPdf/deleteInvoice',
    async ({id, roleSign}) => {
        try {
            const response = await api.delete(`/${roleSign}/invoices/${id}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Mark as paid invoice
export const markInvoiceAsPaid = createAsyncThunk('manualPdf/MarkInvoiceAsPaid',
    async ({id, roleSign, data}) => {
        try {
            let response = await api.patch(`/${roleSign}/invoices/${id}`, data);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

const manualPdfSlice = createSlice({
    name: "mannualPdf",
    initialState,
    reducers: {
        resetCreateStatus(state) {
            state.createStatus = 'idle';
        },
        createInitialData(state, action) {
            state.initialData = {};
            state.initialData = action.payload;
        },
        resetDeleteStatus(state) {
            state.deleteStatus = 'idle';
        },
        resetUpdateStatus(state) {
            state.updateStatus = 'idle';
        },
        resetMarkasPaidStatus(state) {
            state.markAsPaidStatus = 'idle';
        },
    },
    extraReducers: (builder) => {
        builder

            //Create final invoice data
            .addCase(createPdfData.pending, (state) => {
                state.fetchStatus = 'loading';
            })
            .addCase(createPdfData.fulfilled, (state, action) => {
                const {createStatus, error} = handleResponseStatus(action.payload, 'createStatus');
                const {data} = action.payload.data;
                state.finalData = createStatus === "success"
                    ? mergeProperties(state.initialData, data)
                    : state.finalData;
                state.createStatus = createStatus;
                state.error = error;
            })
            .addCase(createPdfData.rejected, (state, action) => {
                state.createStatus = 'failed';
                state.error = action.error.message;
            })

            // Fetch all invoices
            .addCase(fetchAllInvoices.pending, (state) => {
                state.fetchStatus = 'loading';
            })
            .addCase(fetchAllInvoices.fulfilled, (state, action) => {
                const {fetchStatus, error} = handleResponseStatus(action.payload, 'fetchStatus');
                const {data, ...pagination} = action.payload.data;
                state.items = fetchStatus === "success" ? updateIdField(data) : state.items;
                state.fetchStatus = fetchStatus;
                state.pagination = pagination;
                state.error = error;
            })
            .addCase(fetchAllInvoices.rejected, (state, action) => {
                state.fetchStatus = 'failed';
                state.error = action.error.message;
            })

            // Fetch Invoices by ID
            .addCase(fetchInvoiceById.pending, (state) => {
                state.fetchStatus = 'loading';
            })
            .addCase(fetchInvoiceById.fulfilled, (state, action) => {
                const {fetchByIdStatus, error} = handleResponseStatus(action.payload, 'fetchByIdStatus');
                state.items = fetchByIdStatus === "success" ? updateItem(action.payload.data.data, state.items) : state.items;
                state.fetchByIdStatus = fetchByIdStatus;
                state.error = error;
            })
            .addCase(fetchInvoiceById.rejected, (state, action) => {
                state.fetchByIdStatus = 'failed';
                state.error = action.error.message;
            })

            // Update Invoice
            .addCase(updateInvoice.pending, (state) => {
                state.updateStatus = 'loading';
            })
            .addCase(updateInvoice.fulfilled, (state, action) => {
                const {updateStatus, error} = handleResponseStatus(action.payload, 'updateStatus');
                const {data} = action.payload.data;
                state.finalData = updateStatus === "success"
                    ? mergeProperties(state.initialData, data)
                    : state.finalData;
                state.updateStatus = updateStatus;
                state.error = error;
            })
            .addCase(updateInvoice.rejected, (state, action) => {
                state.updateStatus = 'failed';
                state.error = action.error.message;
            })

            // Delete Invoice
            .addCase(deleteInvoice.pending, (state) => {
                state.deleteStatus = 'loading';
            })
            .addCase(deleteInvoice.fulfilled, (state, action) => {
                const {deleteStatus, error} = handleResponseStatus(action.payload, 'deleteStatus');
                const id = action.meta.arg.id;
                if (deleteStatus === "success") {
                    state.items = state.items.filter(item => item.id !== id);
                }
                state.deleteStatus = deleteStatus;
                state.error = error;
            })
            .addCase(deleteInvoice.rejected, (state, action) => {
                state.deleteStatus = 'failed';
                state.error = action.error.message;
            })

            // Mark invoice as paid
            .addCase(markInvoiceAsPaid.pending, (state) => {
                state.markAsPaidStatus = 'loading';
            })
            .addCase(markInvoiceAsPaid.fulfilled, (state, action) => {
                const {markAsPaidStatus, error} = handleResponseStatus(action.payload, 'markAsPaidStatus');
                state.markAsPaidStatus = markAsPaidStatus;
                state.error = error;
            })
            .addCase(markInvoiceAsPaid.rejected, (state, action) => {
                state.markAsPaidStatus = 'failed';
                state.error = action.error.message;
            });
    }
});

export const {
    resetCreateStatus,
    createInitialData,
    resetDeleteStatus,
    resetUpdateStatus,
    resetMarkasPaidStatus
} = manualPdfSlice.actions;
export default manualPdfSlice.reducer;
